import { UnitType } from "@kuika/kuika-cl-model/src/models/screen-canvas-sizes";
import * as React from "react";
import { GuideLineType } from "./guide-line";

export interface IVerticalRulerProps {
  height: number;
  zoomScale: number;
  unit: UnitType;
  offset?: number;
  pageBreakPositions?: number[];
  onCreateGuideLine?: (position: number, type: GuideLineType) => void;
  onPreviewGuideLine?: (position: number, type: GuideLineType) => void;
  onCancelPreview?: () => void;
}

export interface IVerticalRulerState {
  isDraggingNewGuide: boolean;
  dragStartX: number;
  lastPreviewPosition: number | null;
}

export class VerticalRuler extends React.Component<IVerticalRulerProps, IVerticalRulerState> {
  private rulerRef = React.createRef<HTMLDivElement>();

  private scrollAttachTimer: number | null = null;

  constructor(props: IVerticalRulerProps) {
    super(props);
    this.state = {
      isDraggingNewGuide: false,
      dragStartX: 0,
      lastPreviewPosition: null
    };
  }

  shouldComponentUpdate(nextProps: IVerticalRulerProps, nextState: IVerticalRulerState): boolean {
    const pageBreakPositionsChanged =
      JSON.stringify(nextProps.pageBreakPositions) !== JSON.stringify(this.props.pageBreakPositions);

    return (
      nextProps.height !== this.props.height ||
      nextProps.zoomScale !== this.props.zoomScale ||
      nextProps.unit !== this.props.unit ||
      nextProps.offset !== this.props.offset ||
      pageBreakPositionsChanged ||
      nextState.isDraggingNewGuide !== this.state.isDraggingNewGuide
    );
  }

  componentDidMount() {
    this.attachScrollListener();
  }

  private scrollListener: (() => void) | null = null;

  attachScrollListener = () => {
    const tryAttach = () => {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

      if (reportWrapper && !this.scrollListener) {
        this.scrollListener = () => {
          this.forceUpdate();
        };
        reportWrapper.addEventListener("scroll", this.scrollListener, { passive: true });
      }
    };

    tryAttach();
    if (!this.scrollAttachTimer) {
      this.scrollAttachTimer = window.setInterval(tryAttach, 500);
    }
  };

  detachScrollListener = () => {
    if (this.scrollListener) {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
      const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

      if (reportWrapper) {
        reportWrapper.removeEventListener("scroll", this.scrollListener as any);
      }
      this.scrollListener = null;
    }
  };

  convertPxToUnit = (px: number): number => {
    switch (this.props.unit) {
      case UnitType.MM:
        return px * 0.264583;
      case UnitType.INCH:
        return px / 96;
      case UnitType.PT:
        return px * 0.75; // 1px = 0.75pt (96 DPI / 72 points per inch)
      default:
        return px;
    }
  };

  getUnitStep = (): number => {
    switch (this.props.unit) {
      case UnitType.MM:
        return 10;
      case UnitType.INCH:
        return 1;
      case UnitType.PT:
        return 50;
      default:
        return 50;
    }
  };

  getPixelStep = (): number => {
    switch (this.props.unit) {
      case UnitType.MM:
        return 37.8;
      case UnitType.INCH:
        return 96;
      case UnitType.PT:
        return 66.67; // 50pt * (96/72) = 66.67px
      default:
        return 50;
    }
  };

  getActualScrollOffset = (): number => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

    if (reportWrapper) {
      return reportWrapper.scrollTop || 0;
    }

    return this.props.offset || 0;
  };

  getHorizontalScrollOffset = (): number => {
    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    const reportWrapper = doc?.getElementsByClassName("kuika_rootDIWrapper")[0] as HTMLElement;

    if (reportWrapper) {
      return reportWrapper.scrollLeft || 0;
    }

    return 0;
  };

  renderTicks = () => {
    const { height, zoomScale } = this.props;
    const offset = this.getActualScrollOffset();
    const pixelStep = this.getPixelStep();
    const unitStep = this.getUnitStep();
    const scaledStep = pixelStep * zoomScale;
    const smallStep = scaledStep / 5;
    const ticks: React.ReactNode[] = [];

    const scaledOffset = offset * zoomScale;
    const start = Math.max(0, Math.floor(scaledOffset / smallStep) * smallStep - smallStep);
    const end = height + scaledOffset + smallStep;

    for (let pos = start; pos <= end; pos += smallStep) {
      const top = pos - scaledOffset;
      if (top < 0 || top > height) continue;

      const unitValue = this.convertPxToUnit(pos / zoomScale);
      const roundedValue = Math.round(unitValue / (unitStep / 5)) * (unitStep / 5);
      const isMainTick = Math.abs(roundedValue % unitStep) < 0.001;
      const isMajorTick = isMainTick && Math.abs(roundedValue % (unitStep * 5)) < 0.001;

      let tickWidth = "4px";
      if (isMainTick) tickWidth = "10px";
      if (isMajorTick) tickWidth = "14px";

      ticks.push(
        <div
          key={pos}
          className="ruler-tick"
          style={{
            top: `${top}px`,
            width: tickWidth,
            height: isMainTick ? "1.25px" : "0.75px",
            opacity: isMainTick ? 0.9 : 0.5,
            backgroundColor: isMainTick ? "var(--ruler-tick-strong)" : undefined
          }}
        >
          {isMajorTick && <span className="ruler-label ruler-label-vertical">{this.formatNumber(roundedValue)}</span>}
          {isMainTick && !isMajorTick && roundedValue !== 0 && (
            <span className="ruler-label ruler-label-vertical ruler-label-small">
              {this.formatNumber(roundedValue)}
            </span>
          )}
        </div>
      );
    }

    return ticks;
  };

  formatNumber = (value: number): string => {
    if (this.props.unit === UnitType.INCH) {
      return value.toFixed(1);
    }
    return Math.round(value).toString();
  };

  renderPageBreakPointers = () => {
    const { pageBreakPositions, zoomScale, height, unit } = this.props;
    if (!pageBreakPositions || pageBreakPositions.length === 0) return null;

    const offset = this.getActualScrollOffset();
    const pointers: React.ReactNode[] = [];
    const scaledOffset = offset * zoomScale;

    pageBreakPositions.forEach((yPosition, index) => {
      const scaledPos = yPosition * zoomScale;
      const top = scaledPos - scaledOffset;

      if (top >= -20 && top <= height + 20) {
        const unitValue = this.convertPxToUnit(yPosition);
        const displayValue = this.formatNumber(unitValue);

        pointers.push(
          <div
            key={`page-break-${index}`}
            className="page-break-pointer"
            style={{
              position: "absolute",
              top: `${top}px`,
              left: 0,
              right: 0,
              height: "0",
              pointerEvents: "none",
              zIndex: 15
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "0",
                top: "-1px",
                width: "100%",
                height: "2px",
                backgroundColor: "var(--red-5)",
                opacity: 0.8
              }}
            />
            <div
              style={{
                position: "absolute",
                left: "2px",
                top: "2px",
                fontSize: "8px",
                fontWeight: 600,
                color: "var(--red-5)",
                // The ruler's own surface, not white: this pill sits on the ruler and has to
                // follow the theme with it.
                backgroundColor: "var(--ruler-bg)",
                padding: "1px 3px",
                borderRadius: "var(--radius-xs)",
                whiteSpace: "nowrap",
                lineHeight: "1",
                boxShadow: "var(--shadow-sm)"
              }}
            >
              {displayValue}
            </div>
          </div>
        );
      }
    });

    return pointers;
  };

  getIsometricMode = (): boolean => {
    const storedValue = localStorage.getItem("isFreePositionAreaEnabled");
    if (storedValue !== null) {
      return storedValue !== "true";
    }
    return true;
  };

  snapToIsometricGrid = (value: number): number => {
    if (!this.getIsometricMode()) {
      return value;
    }
    return Math.round(value / 10) * 10;
  };

  handleMouseDown = (e: React.MouseEvent) => {
    if (!this.props.onCreateGuideLine) return;

    e.preventDefault();
    e.stopPropagation();

    const rulerRect = this.rulerRef.current?.getBoundingClientRect();
    const scrollOffset = this.getHorizontalScrollOffset();
    let initialPosition = rulerRect
      ? Math.max(0, (e.clientX - rulerRect.right) / this.props.zoomScale + scrollOffset)
      : 0;

    initialPosition = this.snapToIsometricGrid(initialPosition);

    this.setState({
      isDraggingNewGuide: true,
      dragStartX: e.clientX,
      lastPreviewPosition: initialPosition
    });

    if (rulerRect) {
      this.props.onPreviewGuideLine?.(initialPosition, GuideLineType.VERTICAL);
    }

    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.addEventListener("mousemove", this.handleIframeMouseMove as any);
      iframeDoc.addEventListener("mouseup", this.handleMouseUp as any);
    }
  };

  handleMouseMove = (e: MouseEvent) => {
    if (!this.state.isDraggingNewGuide) return;

    const rulerRect = this.rulerRef.current?.getBoundingClientRect();
    if (!rulerRect) return;

    const scrollOffset = this.getHorizontalScrollOffset();
    let position = Math.max(0, (e.clientX - rulerRect.right) / this.props.zoomScale + scrollOffset);
    position = this.snapToIsometricGrid(position);
    this.setState({ lastPreviewPosition: position });
    this.props.onPreviewGuideLine?.(position, GuideLineType.VERTICAL);
  };

  handleIframeMouseMove = (e: MouseEvent) => {
    if (!this.state.isDraggingNewGuide) return;

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    if (!iframe) return;

    const iframeRect = iframe.getBoundingClientRect();
    const rulerRect = this.rulerRef.current?.getBoundingClientRect();
    if (!rulerRect) return;

    const scrollOffset = this.getHorizontalScrollOffset();
    const adjustedClientX = e.clientX + iframeRect.left;
    let position = Math.max(0, (adjustedClientX - rulerRect.right) / this.props.zoomScale + scrollOffset);
    position = this.snapToIsometricGrid(position);
    this.setState({ lastPreviewPosition: position });
    this.props.onPreviewGuideLine?.(position, GuideLineType.VERTICAL);
  };

  handleMouseUp = () => {
    if (this.state.isDraggingNewGuide) {
      const deltaX =
        this.state.lastPreviewPosition !== null ? Math.abs(this.state.lastPreviewPosition * this.props.zoomScale) : 0;

      if (this.state.lastPreviewPosition !== null && deltaX > 5) {
        this.props.onCreateGuideLine?.(this.state.lastPreviewPosition, GuideLineType.VERTICAL);
      } else {
        this.props.onCancelPreview?.();
      }
    }

    this.setState({
      isDraggingNewGuide: false,
      lastPreviewPosition: null
    });
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.removeEventListener("mousemove", this.handleIframeMouseMove as any);
      iframeDoc.removeEventListener("mouseup", this.handleMouseUp as any);
    }
  };

  componentWillUnmount() {
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);

    const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
    const iframeDoc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.removeEventListener("mousemove", this.handleIframeMouseMove as any);
      iframeDoc.removeEventListener("mouseup", this.handleMouseUp as any);
    }

    if (this.scrollAttachTimer) {
      window.clearInterval(this.scrollAttachTimer);
      this.scrollAttachTimer = null;
    }

    this.detachScrollListener();
  }

  render() {
    const { height } = this.props;
    const { isDraggingNewGuide } = this.state;

    return (
      <div
        ref={this.rulerRef}
        className="vertical-ruler"
        style={{
          height: `${height}px`,
          position: "relative",
          overflow: "hidden",
          cursor: isDraggingNewGuide ? "grabbing" : "grab"
        }}
        onMouseDown={this.handleMouseDown}
      >
        {this.renderTicks()}
        {this.renderPageBreakPointers()}
      </div>
    );
  }
}
